'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Car, Fuel, ArrowRight, ChevronRight, Heart, Shield, TrendingUp } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface Vehicle {
  id: string;title: string;price: number;location: string;make: string;
  model: string;year: number;mileage: string;fuel: string;transmission: string;
  condition: string;image_url: string;is_featured: boolean;seller: string;verified: boolean;
}

const VEHICLES: Vehicle[] = [
{ id: '1', title: 'Toyota Vitz 2018 KDJ', price: 850000, location: 'Nairobi', make: 'Toyota', model: 'Vitz', year: 2018, mileage: '45,000 km', fuel: 'Petrol', transmission: 'Automatic', condition: 'Used', image_url: "https://images.unsplash.com/photo-1679421538072-f5bc29222f23", is_featured: true, seller: 'Samuel K', verified: true },
{ id: '2', title: 'Subaru Forester 2016 XT', price: 1450000, location: 'Westlands', make: 'Subaru', model: 'Forester', year: 2016, mileage: '72,000 km', fuel: 'Petrol', transmission: 'Automatic', condition: 'Used', image_url: "https://images.unsplash.com/photo-1679421538072-f5bc29222f23", is_featured: false, seller: 'Peter M', verified: true },
{ id: '3', title: 'Nissan Note 2019 E-Power', price: 1100000, location: 'Mombasa', make: 'Nissan', model: 'Note', year: 2019, mileage: '28,000 km', fuel: 'Hybrid', transmission: 'Automatic', condition: 'Used', image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b38c8af9-1784150139386.png", is_featured: true, seller: 'Grace O', verified: false },
{ id: '4', title: 'Toyota Land Cruiser V8 2015', price: 6500000, location: 'Karen', make: 'Toyota', model: 'Land Cruiser', year: 2015, mileage: '95,000 km', fuel: 'Diesel', transmission: 'Automatic', condition: 'Used', image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bf2e0883-1783837235616.png', is_featured: true, seller: 'David O', verified: true },
{ id: '5', title: 'Honda Fit 2017 RS', price: 780000, location: 'Kisumu', make: 'Honda', model: 'Fit', year: 2017, mileage: '55,000 km', fuel: 'Petrol', transmission: 'Automatic', condition: 'Used', image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bf2e0883-1783837235616.png', is_featured: false, seller: 'Mary W', verified: false },
{ id: '6', title: 'Mazda CX-5 2020 AWD', price: 2800000, location: 'Nairobi', make: 'Mazda', model: 'CX-5', year: 2020, mileage: '18,000 km', fuel: 'Petrol', transmission: 'Automatic', condition: 'Used', image_url: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bf2e0883-1783837235616.png', is_featured: false, seller: 'John K', verified: true }];


const MAKES = ['All Makes', 'Toyota', 'Subaru', 'Nissan', 'Honda', 'Mazda', 'Mitsubishi', 'BMW', 'Mercedes'];
const FUEL_TYPES = ['All Fuel', 'Petrol', 'Diesel', 'Hybrid', 'Electric'];
const TRANSMISSIONS = ['All', 'Automatic', 'Manual'];
const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Westlands', 'Karen'];
const YEAR_RANGES = ['Any Year', '2020+', '2018–2020', '2015–2017', '2010–2014', 'Before 2010'];

export default function VehiclesLandingPage() {
  const [search, setSearch] = useState('');
  const [make, setMake] = useState('All Makes');
  const [fuel, setFuel] = useState('All Fuel');
  const [transmission, setTransmission] = useState('All');
  const [location, setLocation] = useState('All Locations');
  const [yearRange, setYearRange] = useState('Any Year');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const filtered = VEHICLES.filter((v) => {
    const matchSearch = !search || v.title.toLowerCase().includes(search.toLowerCase()) || v.make.toLowerCase().includes(search.toLowerCase());
    const matchMake = make === 'All Makes' || v.make === make;
    const matchFuel = fuel === 'All Fuel' || v.fuel === fuel;
    const matchTrans = transmission === 'All' || v.transmission === transmission;
    const matchLoc = location === 'All Locations' || v.location.includes(location);
    const matchMin = !minPrice || v.price >= Number(minPrice);
    const matchMax = !maxPrice || v.price <= Number(maxPrice);
    let matchYear = true;
    if (yearRange !== 'Any Year') {
      if (yearRange === '2020+') matchYear = v.year >= 2020;else
      if (yearRange === 'Before 2010') matchYear = v.year < 2010;else
      {const [min, max] = yearRange.split('–').map(Number);matchYear = v.year >= min && v.year <= max;}
    }
    return matchSearch && matchMake && matchFuel && matchTrans && matchLoc && matchMin && matchMax && matchYear;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #071428 0%, #0a0d1a 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #2196F3 0%, transparent 50%), radial-gradient(circle at 75% 30%, #00BCD4 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
              style={{ background: 'rgba(33,150,243,0.15)', color: '#2196F3', border: '1px solid rgba(33,150,243,0.3)' }}>
                <Car size={12} /> {8420 .toLocaleString()} Vehicles Listed
              </div>
              <Link href="/create-listing?type=vehicle" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
              style={{ background: '#2196F3', color: '#fff' }}>
                + Sell Vehicle
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Find Your <span style={{ color: '#2196F3' }}>Perfect Car</span> in Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Browse thousands of verified vehicles. New arrivals daily from Japan, UK & local dealers.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search make, model, year…"
              className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {MAKES.map((m) =>
            <button key={m} onClick={() => setMake(m)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: make === m ? 'rgba(33,150,243,0.15)' : 'rgba(255,255,255,0.04)', color: make === m ? '#2196F3' : 'rgba(255,255,255,0.55)', border: `1px solid ${make === m ? 'rgba(33,150,243,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {m}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          {/* Sub-filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[
            { value: fuel, options: FUEL_TYPES, onChange: setFuel },
            { value: transmission, options: TRANSMISSIONS, onChange: setTransmission },
            { value: location, options: LOCATIONS, onChange: setLocation },
            { value: yearRange, options: YEAR_RANGES, onChange: setYearRange }].
            map((sel, i) =>
            <select key={i} value={sel.value} onChange={(e) => sel.onChange(e.target.value)}
            className="px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
                {sel.options.map((o) => <option key={o} value={o} style={{ background: '#0D1526' }}>{o}</option>)}
              </select>
            )}
            <input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="Min KES"
            className="w-24 px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
            <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max KES"
            className="w-24 px-3 py-2 rounded-xl text-xs outline-none"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#2196F3', fontWeight: 700 }}>{filtered.length}</span> vehicles found
            </span>
            <Link href="/login" className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#2196F3', color: '#fff' }}>
              List Your Car <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((v) =>
            <div key={v.id} className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${v.is_featured ? 'rgba(33,150,243,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="relative aspect-video">
                  <img src={v.image_url} alt={`${v.title} - ${v.year} ${v.make} ${v.model} for sale in ${v.location}`}
                className="w-full h-full object-cover" loading="lazy" />
                  {v.is_featured &&
                <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: '#2196F3', color: '#fff' }}>Featured</span>
                }
                  <button onClick={() => toggleSave(v.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: savedIds.has(v.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                    <Heart size={13} fill={savedIds.has(v.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                  </button>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-700 text-white line-clamp-1">{v.title}</p>
                      <p className="text-base font-800 mt-0.5" style={{ color: '#2196F3' }}>KES {v.price.toLocaleString()}</p>
                    </div>
                    {v.verified &&
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,200,83,0.15)' }}>
                        <Shield size={11} style={{ color: '#00C853' }} />
                      </div>
                  }
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[v.year, v.mileage, v.fuel, v.transmission].map((tag) =>
                  <span key={String(tag)} className="text-[10px] px-2 py-0.5 rounded-lg font-600"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>{tag}</span>
                  )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1">
                      <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{v.location}</span>
                    </div>
                    <Link href="/login" className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                  style={{ background: 'rgba(33,150,243,0.15)', color: '#2196F3', border: '1px solid rgba(33,150,243,0.25)' }}>
                      View <ChevronRight size={11} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(33,150,243,0.1), rgba(0,188,212,0.1))', border: '1px solid rgba(33,150,243,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {8420 .toLocaleString()} Vehicles</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to save vehicles, contact sellers, and get price alerts.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#2196F3', color: '#fff' }}>
                <Car size={16} /> Browse All Cars
              </Link>
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                <TrendingUp size={16} /> List Your Car
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>);

}